/* eslint-disable react/no-this-in-sfc */
/* eslint-disable no-unused-vars */
import React, { useContext, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useTheme } from "react-jss";
import { Fade, Paper, useMediaQuery } from "@material-ui/core";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useTranslation } from "react-i18next";
import SyncLoader from "react-spinners/SyncLoader";
import useStyles from "./styles";
import SvgClose from "../../../Icons/Close";
import { breakpoints, sizes } from "../../../../constants/constraints";
import FiltersContext from "../../../../contexts/filtering";
import useOptions from "../../../../hooks/options";

/**
 * This function renders a container to comport the pixel chart.
 * @returns The chart container.
 */
function ClassificationChartContainer({
  containerRef,
  chartData,
  handleClose,
  opened,
  loading,
}) {
  ClassificationChartContainer.defaultProps = {
    chartData: undefined,
  };

  ClassificationChartContainer.propTypes = {
    chartData: PropTypes.shape(),
    handleClose: PropTypes.func.isRequired,
    opened: PropTypes.bool.isRequired,
    loading: PropTypes.bool.isRequired,
    containerRef: PropTypes.any.isRequired,
  };

  const classes = useStyles();
  const {
    values: { statisticsOpened, initiative },
  } = useContext(FiltersContext);

  const theme = useTheme();
  const defaultOptions = useOptions();
  const isSmd = useMediaQuery(breakpoints.max.smd);
  const isSm = useMediaQuery(breakpoints.max.sm);
  const isXsm = useMediaQuery(breakpoints.max.xsm);
  const { t } = useTranslation();
  let options = {};
  const isPeru = initiative === "peru";
  if (chartData) {
    options = {
      credits: defaultOptions.credits,
      chart: {
        type: "line",
        ...defaultOptions.chart,
        height: 180,
        width: 340,
        spacing: [10, 10, 15, 10],
      },
      title: {
        text: t("dashboard.map.pixelSelection.title"),
        ...defaultOptions.title,
      },
      lang: defaultOptions.lang,
      xAxis: {
        categories: chartData.x,
        zoomEnabled: false,
        labels: {
          style: {
            fontSize: "10px",
            color: theme.text.secondary,
            ...defaultOptions.yAxis.labels.style,
          },
          formatter() {
            if (chartData.timeMode === "monthly") {
              return t("general.date.monthYear", {
                date: new Date(this.value),
              });
            }
            return t("general.date.year", { date: this.value });
          },
        },
        gridLineWidth: 0,
        gridLineColor: theme.filters.timeline.separator,
      },
      yAxis: {
        title: {
          enabled: false,
        },
        max: 4,
        min: 0,
        zoomEnabled: false,
        labels: {
          enabled: false,
          style: {
            fontSize: "10px",
            color: theme.text.tertiary,
            ...defaultOptions.xAxis.labels.style,
          },
        },
        gridLineWidth: 0,
        gridLineColor: theme.filters.timeline.separator,
        width: isXsm ? "40%" : isSm ? "70%" : isSmd ? "80%" : "70%",
      },
      series: [
        {
          name: isPeru
            ? t("dashboard.map.legend.waterClassification.peru.natural")
            : t("dashboard.map.legend.waterClassification.natural"),
          color: theme.waterClassification.natural,
          data: chartData.y.map((v) => {
            const item = { y: v };
            if (isPeru) {
              if (v === 1) {
                item.color = theme.waterClassification.natural;
              } else if (v === 2) {
                item.color = theme.waterClassification.anthropic;
              } else if (v === 3) {
                item.color = theme.waterClassification.hydroelectric;
              } else if (v === 4) {
                item.color = theme.waterClassification.mining;
              } else if (v === 5) {
                item.color = theme.waterClassification.aquaculture;
              } else if (v === 6) {
                item.color = theme.waterClassification.regulatedLagoons;
              } else if (v === 7) {
                item.color = theme.waterClassification.glacialLagoons;
              } else {
                item.color = theme.text.primary;
              }
            } else {
              // eslint-disable-next-line no-lonely-if
              if (v === 1) {
                item.color = theme.waterClassification.natural;
              } else if (v === 2) {
                item.color = theme.waterClassification.anthropic;
              } else if (v === 3) {
                item.color = theme.waterClassification.hydroelectric;
              } else if (v === 4) {
                item.color = theme.waterClassification.mining;
              } else if (v === 5) {
                item.color = theme.waterClassification.aquaculture;
              } else {
                item.color = theme.text.primary;
              }
            }

            return item;
          }),
          states: {
            inactive: {
              opacity: 1,
            },
          },
        },
        ...(isPeru
          ? [
              {
                name: t(
                  "dashboard.map.legend.waterClassification.peru.regulatedLagoons"
                ),
                color: theme.waterClassification.regulatedLagoons,
                data: [],
              },
              {
                name: t(
                  "dashboard.map.legend.waterClassification.peru.glacialLagoons"
                ),
                color: theme.waterClassification.glacialLagoons,
                data: [],
              },
            ]
          : []),
        {
          name: t(
            isPeru
              ? "dashboard.map.legend.waterClassification.peru.anthropic"
              : "dashboard.map.legend.waterClassification.anthropic"
          ),
          color: theme.waterClassification.anthropic,
          data: [],
        },
        {
          name: t(
            isPeru
              ? "dashboard.map.legend.waterClassification.peru.hydroelectric"
              : "dashboard.map.legend.waterClassification.hydroelectric"
          ),
          color: theme.waterClassification.hydroelectric,
          data: [],
        },
        {
          name: t(
            isPeru
              ? "dashboard.map.legend.waterClassification.peru.mining"
              : "dashboard.map.legend.waterClassification.mining"
          ),
          color: theme.waterClassification.mining,
          data: [],
        },
        {
          name: t(
            isPeru
              ? "dashboard.map.legend.waterClassification.peru.aquaculture"
              : "dashboard.map.legend.waterClassification.aquaculture"
          ),
          color: theme.waterClassification.aquaculture,
          data: [],
        },
        {
          name: t("dashboard.map.pixelSelection.other"),
          color: theme.text.primary,
          data: [],
        },
      ],
      tooltip: {
        useHTML: true,
        formatter() {
          let label = "";

          if (isPeru) {
            label =
              this.point.y === 1
                ? t("dashboard.map.legend.waterClassification.peru.natural")
                : this.point.y === 2
                ? t("dashboard.map.legend.waterClassification.peru.anthropic")
                : this.point.y === 3
                ? t(
                    "dashboard.map.legend.waterClassification.peru.hydroelectric"
                  )
                : this.point.y === 4
                ? t("dashboard.map.legend.waterClassification.peru.mining")
                : this.point.y === 5
                ? t("dashboard.map.legend.waterClassification.peru.aquaculture")
                : this.point.y === 6
                ? t(
                    "dashboard.map.legend.waterClassification.peru.regulatedLagoons"
                  )
                : this.point.y === 7
                ? t(
                    "dashboard.map.legend.waterClassification.peru.glacialLagoons"
                  )
                : t("dashboard.map.pixelSelection.other");
          } else {
            // eslint-disable-next-line no-lonely-if
            label =
              this.point.y === 1
                ? t("dashboard.map.legend.waterClassification.natural")
                : this.point.y === 2
                ? t("dashboard.map.legend.waterClassification.anthropic")
                : this.point.y === 3
                ? t("dashboard.map.legend.waterClassification.hydroelectric")
                : this.point.y === 4
                ? t("dashboard.map.legend.waterClassification.mining")
                : this.point.y === 5
                ? t("dashboard.map.legend.waterClassification.aquaculture")
                : t("dashboard.map.pixelSelection.other");
          }

          return `<b>${this.point.category}</b><br/>
            <tr><td style="color: ${this.series.color}">${label}</td></tr>`;
        },
      },

      plotOptions: {
        line: {
          states: {
            hover: {
              lineWidthPlus: 0,
            },
          },
          lineWidth: 0,
          marker: {
            enabled: true,
            lineWidth: 0,
          },
        },
        series: {
          animation: false,
          events: {
            legendItemClick(e) {
              e.preventDefault();
            },
          },
        },
      },
      legend: {
        margin: 0,
        itemStyle: { color: theme.text.primary },
        itemHoverStyle: { color: theme.text.primary },
      },
      navigation: defaultOptions.navigation,
      exporting: {
        ...defaultOptions.exporting,
        buttons: {
          contextButton: {
            x: -33,
            ...defaultOptions.exporting.buttons.contextButton,
          },
        },
      },
    };
  }

  return (
    <Fade in={opened || loading}>
      <Paper>
        <div
          className={classes.container}
          style={
            isSmd
              ? {}
              : {
                  right:
                    sizes.map.navigation.itemsMargin +
                    (statisticsOpened
                      ? sizes.statistiscs.width +
                        sizes.map.navigation.itemsMargin
                      : 0),
                }
          }
          ref={containerRef}
        >
          {!loading && (
            <div
              role="button"
              className={classes.clickableButton}
              onClick={() => {
                handleClose();
              }}
              onKeyDown={() => {}}
              tabIndex={0}
            >
              <SvgClose
                className={classes.navigationIcon}
                style={{ transform: "scale(1)" }}
              />
            </div>
          )}
          {loading && (
            <div className={classes.gifLoaderWrapper}>
              <SyncLoader color={theme.primary} size={10} />
            </div>
          )}
          {!loading && (
            <HighchartsReact highcharts={Highcharts} options={options} />
          )}
        </div>
      </Paper>
    </Fade>
  );
}

export default ClassificationChartContainer;
